import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Package, Boxes, MapPin, CalendarCheck, LayoutDashboard,
  ArrowLeft, Layers, Radio, CalendarDays, Truck, FileText, Settings,
  ClipboardList, Sparkles, Printer, Handshake, ScanLine, Wallet, AlertTriangle, Inbox, BookOpen,
  ChevronDown, Image as ImageIcon, Navigation, Route as RouteIcon, Megaphone, ArrowUpCircle, BellRing, MessageSquareText,
  Menu, X, PackageCheck, CalendarRange,
} from "lucide-react";
import { Btn } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthProvider";
import { API } from "@/config";
import { NotificationBell } from "@/components/admin/NotificationBell";

export const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#B9C8D5]">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
};

// Shared nav groups — used by both desktop sidebar and mobile drawer
const NAV_GROUPS = [
  {
    id: "gear",
    label: "Gear",
    icon: Package,
    items: [
      { to: "/admin/products", label: "Products", icon: Package },
      { to: "/admin/bundles", label: "Bundles", icon: Layers },
      { to: "/admin/inventory", label: "Inventory", icon: Boxes },
      { to: "/admin/categories", label: "Categories", icon: Layers },
      { to: "/admin/stock-requests", label: "Stock Requests", icon: BellRing },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: Truck,
    items: [
      { to: "/admin/reservations", label: "Reservations", icon: CalendarCheck },
      { to: "/admin/live", label: "Live Rentals", icon: Radio },
      { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/admin/drivers", label: "Drivers", icon: Truck },
      { to: "/admin/route-builder", label: "Route Builder", icon: RouteIcon },
      { to: "/admin/stage-prep", label: "Stage Prep", icon: PackageCheck },
      { to: "/admin/my-route", label: "My Route", icon: Navigation },
      { to: "/admin/scan", label: "Scan & Check In", icon: ScanLine },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: Wallet,
    items: [
      { to: "/admin/payouts", label: "Payouts", icon: Wallet },
      { to: "/admin/tax-report", label: "Tax Report", icon: FileText },
      { to: "/admin/payout-scenarios", label: "Payout Scenarios", icon: FileText },
      { to: "/admin/refund-report", label: "Refund Report", icon: ArrowUpCircle },
      { to: "/admin/promos", label: "Promotions", icon: Megaphone },
      { to: "/admin/promo-codes", label: "Promo Codes", icon: Sparkles },
      { to: "/admin/partners", label: "Affiliates / Partners", icon: Handshake },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: BookOpen,
    items: [
      { to: "/admin/blog", label: "Field Journal", icon: BookOpen },
      { to: "/admin/content", label: "Content", icon: ImageIcon },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    items: [
      { to: "/admin/booking-settings", label: "Booking Settings", icon: Settings },
      { to: "/admin/message-templates", label: "Message Templates", icon: MessageSquareText },
      { to: "/admin/zones", label: "Delivery Zones", icon: MapPin },
      { to: "/admin/labels", label: "Customer Tags", icon: Printer },
      { to: "/admin/third-party-labels", label: "3rd Party Labels", icon: CalendarRange },
      { to: "/admin/run-sheet", label: "Run Sheet", icon: ClipboardList },
      { to: "/admin/inbox", label: "Owner Inbox", icon: Inbox, badgeKey: "inbox" },
    ],
  },
];

const testId = (label) => label.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-");

const loadOpenGroups = (pathname) => {
  try {
    const raw = localStorage.getItem("admin_nav_open_groups");
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const active = NAV_GROUPS.find(g => g.items.some(i => pathname.startsWith(i.to)));
  return active ? { [active.id]: true } : { operations: true };
};

// Shared nav tree used by both sidebar and mobile drawer
function NavTree({ openGroups, toggleGroup, badgeFor, location, onNavClick }) {
  return (
    <>
      <NavLink
        to="/admin"
        end
        onClick={onNavClick}
        className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors mb-2 ${isActive ? "bg-[#FFD166]/12 text-[#FFD166] border border-[#FFD166]/30" : "text-[#B9C8D5] hover:text-[#F6F1E4] hover:bg-white/5 border border-transparent"}`}
        data-testid="admin-nav-dashboard"
      >
        <LayoutDashboard className="w-4 h-4" />
        <span className="flex-1">Dashboard</span>
      </NavLink>
      <div className="space-y-1">
        {NAV_GROUPS.map((g) => {
          const isOpen = !!openGroups[g.id];
          const groupBadge = g.items.reduce((sum, i) => sum + (i.badgeKey ? badgeFor(i.badgeKey) : 0), 0);
          const hasActive = g.items.some(i => location.pathname === i.to || location.pathname.startsWith(i.to + "/"));
          return (
            <div key={g.id} data-testid={`admin-nav-group-${g.id}`}>
              <button
                type="button"
                onClick={() => toggleGroup(g.id)}
                aria-expanded={isOpen}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors border ${hasActive ? "border-[#FFD166]/30 bg-[#FFD166]/5 text-[#F6F1E4]" : "border-transparent text-[#B9C8D5] hover:text-[#F6F1E4] hover:bg-white/5"}`}
                data-testid={`admin-nav-group-toggle-${g.id}`}
              >
                <g.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left uppercase tracking-[0.14em] text-[11px] font-semibold">{g.label}</span>
                {groupBadge > 0 && !isOpen && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500/90 text-[#F6F1E4] text-[10px] font-semibold border border-red-400/60">
                    {groupBadge > 99 ? "99+" : groupBadge}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="pl-3 mt-1 space-y-0.5 border-l border-[#FFD166]/15 ml-4">
                  {g.items.map((l) => {
                    const badge = l.badgeKey ? badgeFor(l.badgeKey) : 0;
                    return (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        onClick={onNavClick}
                        className={({ isActive }) => `flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-[#FFD166]/12 text-[#FFD166]" : "text-[#B9C8D5] hover:text-[#F6F1E4] hover:bg-white/5"}`}
                        data-testid={`admin-nav-${testId(l.label)}`}
                      >
                        <l.icon className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />
                        <span className="flex-1">{l.label}</span>
                        {badge > 0 && (
                          <span
                            className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500/90 text-[#F6F1E4] text-[10px] font-semibold border border-red-400/60"
                            data-testid={`admin-nav-badge-${testId(l.label)}`}
                          >
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export const AdminShell = ({ children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [health, setHealth] = useState({ failed_count: 0, failed_cents: 0 });
  const [queueBadges, setQueueBadges] = useState({ refunds: 0, changes: 0 });
  const [openGroups, setOpenGroups] = useState(() => loadOpenGroups(location.pathname));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync open groups to localStorage and auto-expand on route change
  useEffect(() => {
    try { localStorage.setItem("admin_nav_open_groups", JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups]);

  // Auto-expand the group that contains the current route when navigating
  useEffect(() => {
    const active = NAV_GROUPS.find(g => g.items.some(i => location.pathname.startsWith(i.to)));
    if (active) setOpenGroups((s) => ({ ...s, [active.id]: true }));
    setMobileOpen(false); // close drawer on route change
  }, [location.pathname]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [h, rq, cq] = await Promise.all([
          axios.get(`${API}/admin/payouts/health`),
          axios.get(`${API}/admin/refund-queue`).catch(() => ({ data: { requests: [] } })),
          axios.get(`${API}/admin/change-requests`).catch(() => ({ data: { requests: [] } })),
        ]);
        if (cancelled) return;
        setHealth(h.data);
        setQueueBadges({
          refunds: (rq.data?.requests || []).length,
          changes: (cq.data?.requests || []).length,
        });
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const badgeFor = (key) => {
    if (key === "inbox") return queueBadges.refunds + queueBadges.changes;
    return 0;
  };

  const toggleGroup = (id) => setOpenGroups((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="admin-shell min-h-screen text-[#F6F1E4]" style={{ background: "linear-gradient(180deg,#040A0F,#0A2438 50%,#040A0F)" }}>
      {/* ── Sticky header ─────────────────────────────────────────────────── */}
      <header className="border-b border-[#FFD166]/15 backdrop-blur bg-[#040A0F]/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          {/* Left: hamburger (mobile) + branding */}
          <div className="flex items-center gap-3">
            {/* Hamburger — only on mobile/tablet */}
            <button
              type="button"
              onClick={() => setMobileOpen((o) => !o)}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-[#FFD166]/20 text-[#FFD166] hover:bg-[#FFD166]/10 transition-colors"
              aria-label="Open navigation"
              data-testid="admin-hamburger"
            >
              <Menu className="w-5 h-5" />
            </button>
            <a href="/" className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1 hidden md:inline-flex">
              <ArrowLeft className="w-3 h-3" /> Site
            </a>
            <span className="text-[#3A5A40] hidden md:inline">·</span>
            <div className="font-serif text-lg md:text-xl" style={{ background: "linear-gradient(135deg,#F4A93A,#FFD166,#D97B37)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Enchanted Admin
            </div>
          </div>
          {/* Right: bell + user + logout */}
          <div className="flex items-center gap-2 md:gap-4">
            <NotificationBell />
            <span className="text-xs text-[#B9C8D5] hidden md:inline">{user?.email}</span>
            <Btn variant="ghost" onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout" className="!px-2 md:!px-3">
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Btn>
          </div>
        </div>
      </header>

      {/* ── Payout health alert ─────────────────────────────────────────── */}
      {health.failed_count > 0 && (
        <Link
          to="/admin/payouts?status=failed"
          className="block bg-red-600/25 border-y border-red-500/50 backdrop-blur-sm hover:bg-red-600/35 transition-colors"
          data-testid="payout-health-alert"
        >
          <div className="max-w-7xl mx-auto px-5 py-3 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-red-200">
                {health.failed_count} Stripe payout {health.failed_count === 1 ? "entry" : "entries"} failed
                {" · "}${(health.failed_cents / 100).toFixed(2)} unsettled
              </div>
              <div className="text-[11px] text-red-200/80">Review the Payouts tab and click Retry to include them in tonight's sweep.</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-red-200 hidden sm:inline">Review →</span>
          </div>
        </Link>
      )}

      {/* ── Mobile nav overlay + drawer ────────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[88vw] bg-[#040A0F] border-r border-[#FFD166]/15 flex flex-col overflow-hidden shadow-2xl"
            style={{ animation: "slideInLeft 200ms ease-out" }}
            data-testid="admin-mobile-drawer"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-[#FFD166]/15 shrink-0">
              <div className="font-serif text-lg" style={{ background: "linear-gradient(135deg,#F4A93A,#FFD166,#D97B37)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Enchanted Admin
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[#B9C8D5] hover:text-white hover:bg-white/10"
                data-testid="admin-mobile-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Nav scroll area */}
            <div className="flex-1 overflow-y-auto p-3">
              <NavTree
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                badgeFor={badgeFor}
                location={location}
                onNavClick={() => setMobileOpen(false)}
              />
            </div>
            {/* Drawer footer */}
            <div className="border-t border-[#FFD166]/15 px-4 py-3 shrink-0">
              <div className="text-xs text-[#B9C8D5] mb-2 truncate">{user?.email}</div>
              <button
                type="button"
                onClick={async () => { setMobileOpen(false); await logout(); nav("/admin/login"); }}
                className="flex items-center gap-2 text-sm text-[#B9C8D5] hover:text-[#FFD166]"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main layout ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-5 py-8 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8 min-w-0">
        {/* Desktop sidebar — hidden on mobile */}
        <nav className="hidden md:block md:sticky md:top-20 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto pr-1" data-testid="admin-sidebar">
          <NavTree
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            badgeFor={badgeFor}
            location={location}
            onNavClick={null}
          />
        </nav>
        <main className="min-w-0">{children}</main>
      </div>

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};
